import { NgOptimizedImage } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  input,
  output,
} from '@angular/core';
import { Router, RouterLink } from '@angular/router';
import { NotificationStore } from '@indutrust/notification-data';
import { NotificationPanelComponent } from '@indutrust/notification-ui';
import {
  I18nStore,
  LayoutStore,
  PlatformStore,
  ThemeStore,
} from '@indutrust/shared-data';
import { APP_BRAND_CONFIG, APP_SESSION } from '@indutrust/shared-model';
import { provideTranslocoScopeSharedGlobal } from '@indutrust/shared-util';
import { TranslocoDirective, TranslocoService } from '@jsverse/transloco';
import type { MenuItem } from 'primeng/api';
import { Menu } from 'primeng/menu';
import { Popover } from 'primeng/popover';
import { Tooltip } from 'primeng/tooltip';
import { I18nComponent } from '../i18n/i18n.component';
import { ProfileHeaderComponent } from '../profile-header/profile-header.component';
import { ThemeToggleComponent } from '../theme-toggle/theme-toggle.component';
import { resolveHelpMenuItems } from './resolve-help-menu-items.util';
import { TOPBAR_HELP_ITEMS_FACTORY } from './topbar-help-items.token';

/* eslint-disable indutrust-custom/prefer-semantic-theme-utilities -- live notification pulse indicator */
@Component({
  selector: 'idt-topbar',
  imports: [
    I18nComponent,
    Menu,
    NgOptimizedImage,
    NotificationPanelComponent,
    Popover,
    ProfileHeaderComponent,
    RouterLink,
    ThemeToggleComponent,
    Tooltip,
    TranslocoDirective,
  ],
  template: `
    <div class="layout-topbar">
      <div class="layout-topbar-logo-container">
        @if (showMenuToggle()) {
          <button
            class="layout-menu-button layout-topbar-action"
            (click)="layoutStore.onMenuToggle()">
            <i class="pi pi-bars"></i>
          </button>
        }
        <a class="layout-topbar-logo" [routerLink]="logoRouterLink()">
          @if (logoSrc(); as resolvedLogoSrc) {
            <img
              height="23"
              priority
              width="170"
              [alt]="brandConfig.appName"
              [ngSrc]="resolvedLogoSrc" />
          } @else {
            <span class="font-semibold text-2xl">{{
              brandConfig.appName
            }}</span>
          }
        </a>
      </div>

      <ng-container *transloco="let t; prefix: 'global'">
        <div class="layout-topbar-actions">
          <div class="flex items-center gap-1">
            @if (showHelpTrigger()) {
              <div class="layout-topbar-help-trigger">
                <button
                  class="layout-topbar-action"
                  tooltipPosition="bottom"
                  type="button"
                  [pTooltip]="t('help.title')"
                  (click)="helpMenu.toggle($event)">
                  <i class="pi pi-question-circle"></i>
                </button>

                <p-menu
                  #helpMenu
                  appendTo="body"
                  styleClass="topbar-help-menu"
                  [model]="helpMenuItems()"
                  [popup]="true" />
              </div>
            }

            @if (showSearch()) {
              <a
                class="layout-topbar-action"
                routerLink="/search"
                tooltipPosition="bottom"
                [pTooltip]="t('search.title')">
                <i class="pi pi-search"></i>
              </a>
            }

            @if (showNotifications()) {
              <div class="layout-topbar-notification-trigger">
                <!-- Notification bell -->
                <button
                  class="layout-topbar-action relative"
                  data-testid="topbar-notifications"
                  tooltipPosition="bottom"
                  type="button"
                  [pTooltip]="t('notifications.title')"
                  (click)="notificationPanel.toggle($event)">
                  <i class="pi pi-bell"></i>
                  @if (notificationStore.unreadCount() > 0) {
                    <span class="absolute top-2 right-1 flex size-2">
                      <span
                        class="animate-ping absolute inline-flex size-full
                        rounded-full bg-red-400 opacity-75 dark:bg-red-300"></span>
                      <span
                        class="relative inline-flex size-2 rounded-full bg-red-500 dark:bg-red-400"></span>
                    </span>
                  }
                </button>

                <idt-notification-panel #notificationPanel />
              </div>
            }

            @if (showPublicDocsSettingsMenu()) {
              <div
                class="layout-topbar-settings-trigger"
                data-testid="topbar-public-docs-settings">
                <button
                  class="layout-topbar-action"
                  data-testid="topbar-public-docs-settings-trigger"
                  tooltipPosition="bottom"
                  type="button"
                  [attr.aria-label]="t('settings.title')"
                  [pTooltip]="t('settings.title')"
                  (click)="settingsPopover.toggle($event)">
                  <i class="pi pi-cog"></i>
                </button>

                <p-popover
                  #settingsPopover
                  styleClass="no-popover-arrow topbar-settings-popover">
                  <div class="flex min-w-48 flex-col gap-3 p-1">
                    <div class="flex items-center gap-1 font-semibold">
                      <i class="pi pi-cog"></i>
                      {{ t('settings.title') }}
                    </div>
                    <div class="flex items-center justify-between gap-3">
                      <span class="flex items-center gap-2 text-sm">
                        <i class="pi pi-language"></i>
                        {{ t('settings.language') }}
                      </span>
                      <idt-i18n />
                    </div>
                    <div class="flex items-center justify-between gap-3">
                      <span class="flex items-center gap-2 text-sm">
                        <i class="pi pi-circle-fill"></i>
                        {{ t('settings.theme') }}
                      </span>
                      <idt-theme-toggle />
                    </div>
                  </div>
                </p-popover>
              </div>
            }

            @if (showPublicDocsSettingsInline()) {
              <div
                class="flex items-center gap-3"
                data-testid="topbar-public-docs-settings">
                <idt-i18n />
                <idt-theme-toggle />
              </div>
            }
          </div>

          @if (showProfileHeader()) {
            <!-- Profile header with popover -->
            <idt-profile-header
              [company]="session.company()"
              [currentLanguage]="i18nStore.language()"
              [isSmallScreen]="platformStore.isSmallScreen()"
              [user]="session.user()"
              (logout)="logoutRequested.emit()"
              (updateLanguage)="i18nStore.updateLanguage($event)" />
          }
        </div>
      </ng-container>
    </div>
  `,
  providers: [provideTranslocoScopeSharedGlobal()],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
/**
 * Shared application topbar component.
 *
 * Reads the brand logo from {@link APP_BRAND_CONFIG} (using `logoDarkUrl`
 * when in dark mode, falling back to `logoUrl`). When `logoUrl` is not
 * provided the `appName` text is rendered instead. Help menu items are
 * supplied per-brand via {@link TOPBAR_HELP_ITEMS_FACTORY}.
 *
 * Reads the current user and company from {@link APP_SESSION} so this
 * component remains decoupled from any concrete auth implementation.
 */
export class TopbarComponent {
  readonly brandConfig = inject(APP_BRAND_CONFIG);
  readonly notificationStore = inject(NotificationStore);
  readonly session = inject(APP_SESSION);

  protected readonly helpItemsFactory = inject(TOPBAR_HELP_ITEMS_FACTORY);
  protected readonly i18nStore = inject(I18nStore);
  protected readonly layoutStore = inject(LayoutStore);
  protected readonly platformStore = inject(PlatformStore);

  private readonly router = inject(Router);
  private readonly themeStore = inject(ThemeStore);
  private readonly transloco = inject(TranslocoService);

  readonly publicDocsShell = input(false);

  readonly logoutRequested = output();

  protected readonly helpMenuItems = computed((): MenuItem[] => {
    this.i18nStore.language();
    return resolveHelpMenuItems(
      this.helpItemsFactory,
      this.transloco,
      this.router
    );
  });
  protected readonly logoRouterLink = computed(() =>
    this.publicDocsShell() ? '/login' : '/'
  );
  readonly logoSrc = computed((): string | null => {
    const { logoUrl, logoDarkUrl } = this.brandConfig;
    if (!logoUrl) {
      return null;
    }
    if (!this.themeStore.isDarkTheme()) {
      return logoUrl;
    }
    return logoDarkUrl ?? logoUrl;
  });
  protected readonly showHelpTrigger = computed(
    () => this.publicDocsShell() || !this.platformStore.isSmallScreen()
  );
  protected readonly showMenuToggle = computed(() => !this.publicDocsShell());
  protected readonly showNotifications = computed(
    () => !this.publicDocsShell()
  );
  protected readonly showProfileHeader = computed(
    () => !this.publicDocsShell()
  );
  protected readonly showPublicDocsSettingsInline = computed(
    () => this.publicDocsShell() && !this.platformStore.isSmallScreen()
  );
  protected readonly showPublicDocsSettingsMenu = computed(
    () => this.publicDocsShell() && this.platformStore.isSmallScreen()
  );
  protected readonly showSearch = computed(() => {
    return false;
    // Uncomment this when search is implemented
    // if (this.publicDocsShell() || this.platformStore.isSmallScreen()) {
    //   return false;
    // }
    // const userType = this.session.user()?.type;
    // return userType === 'buyer' || userType === 'admin';
  });

  protected readonly loadNotificationsEffect = effect(() => {
    if (!this.publicDocsShell()) {
      this.notificationStore.loadAll();
    }
  });
}
